import { PortfolioSummary as SharedPortfolioSummary } from "@shared/widgets/yaas/PortfolioSummary";
import { FEATURE_FLAGS } from "@solomon-apps/config";
import type { YaasWalletStats } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import React from "react";

import { useDistributions } from "@/hooks/yaas/useDistributions";
import { useWalletView } from "@/hooks/yaas/useWalletView";

import type { DistributionMode } from "../application/types";
import { RetailClaimButton } from "../components/RetailClaimButton";
import { RetailPendingEarningsTicker } from "../components/RetailPendingEarningsTicker";
import { minBalanceNotice } from "../helpers/formatMinBalance";

/**
 * apps/main data wrapper for the lifted, SDK-free shared
 * `PortfolioSummary` card. Keeps the original public API (including the
 * SDK-bound `address` / `earliestPositionCreatedAt` props that drive
 * the live pending ticker) so callers (`LeftHero`) are unchanged.
 *
 * The shared card is mechanism-only: it DI's the pending ticker. This
 * wrapper builds the real `RetailPendingEarningsTicker` (which calls
 * `useDistributions` + computes the live accrual) from those props and
 * injects it via `pendingTicker`. External sim consumers render the
 * shared card directly with a mock `pendingValue` instead.
 */
export function PortfolioSummary({
    walletStats,
    averageAPY,
    address,
    earliestPositionCreatedAt,
    distributionMode,
    isBelowMinimum = false,
    isDisabled = false,
    walletBalance = 0,
    minBalance,
    nextDistributionDate,
    projectedNextDistributionDate,
    distributionPeriodStartsAt
}: {
    walletStats: YaasWalletStats | null;
    averageAPY: string | null;
    address: string;
    earliestPositionCreatedAt: string | null;
    distributionMode: DistributionMode;
    /** Below the minimum balance: "Buy More USDv" is the priority
     *  action, so Claim de-emphasises rather than competing with it. */
    isBelowMinimum?: boolean;
    /** The position's API status is Disabled. Accrual has stopped. */
    isDisabled?: boolean;
    /** On-chain USDv the wallet holds. Stands in for the accrual base
     *  when no Program Position exists — see below. */
    walletBalance?: BigDecimal | number;
    /** Quoted in the Pending tooltip when below the minimum. */
    minBalance?: number;
    nextDistributionDate?: string | null;
    projectedNextDistributionDate?: string | null;
    /** ISO start of the period that pays at `nextDistributionDate`, so
     *  the countdown bar fills across the Program's real period. */
    distributionPeriodStartsAt?: string | null;
}): React.ReactElement {
    const { data: payouts } = useDistributions({ address, limit: 1 });
    const hasPayouts = (payouts?.total ?? 0) > 0;
    const hasPaidRewards =
        hasPayouts || (walletStats?.totalYieldEarned ?? 0) > 0;
    const wallet = useWalletView(address, hasPaidRewards);
    const historicalApr =
        !wallet.isError &&
        !wallet.isPending &&
        wallet.data?.walletAddress === address
            ? (wallet.data.account?.historicalAprPct ?? null)
            : null;
    const stats: YaasWalletStats = walletStats ?? {
        totalPositions: 0,
        formattedTotalPositions: "$0.00",
        totalYieldEarned: 0,
        formattedTotalYieldEarned: "$0.00",
        totalTvl: 0,
        formattedTvl: "$0"
    };

    // null is unresolved LP evidence; only a known zero may use the pre-position wallet balance.
    const principal =
        stats.totalPositionsExact === undefined
            ? new BigDecimal(stats.totalPositions)
            : stats.totalPositionsExact;
    const accrualBase =
        principal === null
            ? new BigDecimal(0)
            : principal.gt(0)
              ? principal
              : new BigDecimal(walletBalance);

    // Accrual stops for a reason, not for the absence of one. Only
    // Disabled and below-minimum stop it; every other enrolled state
    // earns, including the window after enrollment where the Program
    // Position has not been materialised yet. Gating on `hasPosition`
    // froze the ticker for a wallet that was enrolled and earning.
    const isEarning = !isDisabled && !isBelowMinimum;

    return (
        <SharedPortfolioSummary
            walletStats={walletStats}
            claimContent={
                <RetailClaimButton
                    address={address}
                    deEmphasized={isBelowMinimum}
                />
            }
            averageAPY={hasPaidRewards ? null : averageAPY}
            historicalApr={hasPaidRewards ? historicalApr : null}
            // Always loaded on this path. `ResolvedView` had an
            // `isLoading` that was a compile-time `false` — the hook's
            // `base` set it and no branch overrode it — so this wrapper
            // never had anything to forward. The shared component and
            // the ticker keep their flags for the sim consumers inside
            // `packages/shared`, which do vary them.
            isLoading={false}
            // Below the minimum the Pending figure is a frozen zero, so
            // the default tooltip would explain an accrual that is not
            // happening. Swap it for the reason it is not.
            pendingTooltip={
                isBelowMinimum &&
                minBalance !== null &&
                minBalance !== undefined
                    ? minBalanceNotice(minBalance)
                    : undefined
            }
            hasActivePosition={isEarning}
            distributionMode={distributionMode}
            // No Stream / Claim choice on /yaas while the flag is off, so
            // the mode label beside Pending goes with it.
            showDistributionMode={FEATURE_FLAGS.retailDistributionModeChoice}
            nextDistributionDate={nextDistributionDate}
            projectedNextDistributionDate={projectedNextDistributionDate}
            distributionPeriodStartsAt={distributionPeriodStartsAt}
            guillocheSrc="/img/guilloche-hero.svg"
            pendingTicker={
                <RetailPendingEarningsTicker
                    walletAddress={address}
                    totalYieldEarned={stats.totalYieldEarned}
                    // `walletStats.totalPositions` is zero until a Program
                    // Position exists: YaasWalletService holds the totals at
                    // "0" for a non-Active account on purpose. The ticker
                    // guards on `positionValue <= 0`, so it never started
                    // during the materialisation window no matter what the
                    // earning flag said. The wallet balance is the same
                    // figure the Positions column already shows.
                    positionValue={accrualBase.toString()}
                    averageAPY={averageAPY}
                    isLoaded
                    hasActivePosition={isEarning}
                    earliestPositionCreatedAt={earliestPositionCreatedAt}
                    variant="number"
                    // Green says "accruing". Below the minimum nothing is
                    // accruing, so the frozen $0 turns error-base and
                    // pulses: a static zero reads as a loading state, a
                    // pulsing red one reads as a stop. `animate-pulse` is
                    // Tailwind's own 2s opacity cycle — opacity only, and
                    // it stands down under reduced motion.
                    color={
                        isBelowMinimum
                            ? "rgb(var(--color-error-base))"
                            : "rgb(var(--color-success-base))"
                    }
                    className={
                        isBelowMinimum
                            ? "animate-pulse motion-reduce:animate-none"
                            : undefined
                    }
                />
            }
        />
    );
}
